import FSRSAnnouncementModal from "@components/fsrsAnnouncementModal";
import { isAuthed } from "ionicApp";
import { useCallback, useEffect, useRef } from "react";
import { useIonCardModalWithDismiss } from "./useIonModalWithDismiss";
import { useFSRSAnnouncement } from "./useVersionAnnouncements";

/**
 * Hook for managing the FSRS announcement modal
 * Handles presentation and dismissal with automatic version tracking
 */
export function useFSRSAnnouncementModal() {
  const { shouldShowFSRSAnnouncement, markFSRSAnnouncementShown } = useFSRSAnnouncement();

  const [present, dismiss] = useIonCardModalWithDismiss(FSRSAnnouncementModal, {
    onDismiss: markFSRSAnnouncementShown,
  });

  const presentFSRSAnnouncement = useCallback(() => {
    if (shouldShowFSRSAnnouncement) {
      present();
    }
  }, [shouldShowFSRSAnnouncement, present]);

  const dismissFSRSAnnouncement = useCallback(() => {
    markFSRSAnnouncementShown();
    dismiss();
  }, [markFSRSAnnouncementShown, dismiss]);

  return {
    shouldShowFSRSAnnouncement,
    presentFSRSAnnouncement,
    dismissFSRSAnnouncement,
  };
}

export function useShowFSRSAnnouncementModalIfAppropriate() {
  const { shouldShowFSRSAnnouncement, presentFSRSAnnouncement } = useFSRSAnnouncementModal();
  const fsrsAnnouncementAttempted = useRef(false);

  // Show FSRS announcement for existing users when appropriate
  useEffect(() => {
    // Only proceed if all conditions are met and we haven't already attempted
    if (shouldShowFSRSAnnouncement && isAuthed() && !fsrsAnnouncementAttempted.current) {
      fsrsAnnouncementAttempted.current = true;

      // Delay slightly to ensure the app is fully loaded and Router is stable
      const timer = setTimeout(() => {
        // Double-check conditions before presenting (in case auth state changed)
        if (isAuthed() && shouldShowFSRSAnnouncement) {
          try {
            presentFSRSAnnouncement();
          } catch (error) {
            console.warn("Failed to present FSRS announcement:", error);
            // Reset the attempt flag if presentation failed
            fsrsAnnouncementAttempted.current = false;
          }
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [shouldShowFSRSAnnouncement, presentFSRSAnnouncement]);
}
